import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

const columnDefinitions: Record<string, Column<FlatMerchantOverrideDataItem>> = {
    modifiedFlag: {
        Header: '',
        accessor: 'modifiedFlag',
        width: 5,
        disableSortBy: true,
        renderType: 'modifiedIcon'
    },
    merchantId: {
        Header: 'SID',
        id: 'merchantId',
        accessor: 'merchantId',
        dataType: 'text',
        minWidth: 48,
        width: 100,
        renderType: 'link',
        renderOptions: {
            to: 'overrides/merchant/$0',
            isString: true
        }
    },
    merchantIdNoLink: {
        Header: 'SID',
        id: 'merchantId',
        accessor: 'merchantId',
        dataType: 'text',
        minWidth: 48,
        width: 100
    },
    merchantName: {
        Header: 'Merchant Name',
        dataType: 'text',
        width: 200,
        id: 'merchantName',
        accessor: 'merchantName'
    },
    merchantDescription: {
        Header: 'Description',
        dataType: 'text',
        width: 200,
        accessor: 'merchantDescription',
        id: 'merchantDescription'
    },
    overallThreshold: {
        Header: 'Threshold',
        dataType: 'number',
        width: 120,
        id: 'overallThreshold',
        accessor: 'overallThreshold'
    },
    activeRules: {
        Header: 'Active Rules',
        dataType: 'number',
        width: 80,
        id: 'activeRules',
        accessor: 'activeRules'
    },
    overrides: {
        Header: 'Overrides',
        dataType: 'number',
        width: 80,
        id: 'overrides',
        accessor: 'overrides'
    },
    lastModified: {
        Header: 'Last Modified',
        dataType: 'datetime',
        renderType: 'datetime',
        sortType: 'datetime',
        width: 100,
        minWidth: 100,
        id: 'lastModified',
        accessor: 'lastModified',
        preformattedValue: (d) => d.lastModified
    },
    status: {
        Header: 'Status',
        id: '_formatted.merchantEnabled',
        accessor: (d) => d._formatted.merchantEnabled,
        dataType: 'text',
        width: 80
    },
    executionOrder: {
        id: 'priority',
        Header: 'Order',
        dataType: 'number',
        accessor: (d) => d.priority
    }
};

type Args = {
    columns: (
        | 'modifiedFlag'
        | 'merchantId'
        | 'merchantIdNoLink'
        | 'merchantName'
        | 'merchantDescription'
        | 'overallThreshold'
        | 'activeRules'
        | 'overrides'
        | 'lastModified'
        | 'status'
        | Column<FlatMerchantOverrideDataItem>
    )[];
    overrides?: Record<string, Column<FlatMerchantOverrideDataItem>>;
};

const useGetMerchantColumns = ({ columns, overrides }: Args) => {
    const { t } = useTranslation();
    return columns.map((col) => {
        const colDef = typeof col === 'string' ? columnDefinitions[col] : col;
        const overrideColDef = (typeof col === 'string' && overrides && overrides[col]) || {};
        const thisHeader = (overrideColDef as Column<FlatMerchantOverrideDataItem>)?.Header || colDef.Header;
        // translate only if thisHeader is a string Header
        const header = typeof thisHeader === 'string' && thisHeader !== '' ? t(thisHeader) : thisHeader;

        return {
            ...(colDef || colDef),
            ...(overrideColDef || {}),
            Header: header
        } as Column<FlatMerchantOverrideDataItem>;
    });
};

export default useGetMerchantColumns;
